import { formatToCurrency, formatToForm } from '@mixins/currency';
import { format as DecimalFormat } from '@mixins/decimal';
import Item, {
    ITEM_TYPE_PART, ITEM_TYPE_SERVICE, ITEM_TYPE_TANK, ITEM_TYPE_TRAILER,
} from '@models/item';
import Model from '@models/model';

export default class InvoiceItem extends Model {
    static className = 'InvoiceItem';

    customer_location_item = {};

    item_adjustments = [];

    last_item_adjustment = [];

    item = Item;

    tax_amount = 0;

    static allowsEdit = true;

    static formFieldsConfig = {
        item_id: {
            type: 'form-input-autocomplete-items',
        },
        gl_code_id: {
            type: 'form-input-autocomplete-gl-codes',
        },
        branch_id: {
            type: 'form-input-autocomplete-branches',
        },
    }

    static allowedFieldEdits = [
        'item_id',
        'gl_code_id',
        'branch_id',
    ];

    constructor(data) {
        super();
        this.setAttributes(data);
    }

    resource() {
        return 'items';
    }

    relations() {
        return {
            children: InvoiceItem,
            item_adjustment: InvoiceItem,
            item_adjustments: InvoiceItem,
        };
    }

    get formFieldCasts() {
        return {
            cost: formatToForm,
            price: formatToForm,
            tax_amount: formatToForm,
            total: formatToForm,
            qty: DecimalFormat,
            refund_qty: DecimalFormat,
        };
    }

    get propCasts() {
        return {
            qty: DecimalFormat,
            refund_qty: DecimalFormat,
        };
    }

    isTank() {
        return this.item_type_id === ITEM_TYPE_TANK || this.item_type_id === ITEM_TYPE_TRAILER;
    }

    isItem() {
        return this.item_type_id === ITEM_TYPE_SERVICE || this.item_type_id === ITEM_TYPE_PART;
    }

    isServiceItem() {
        return this.item_type_id === ITEM_TYPE_SERVICE;
    }

    getItem() {
        if (this.item_id) {
            return this.item;
        }
        if (this.customer_location_item_id) {
            return this.customer_location_item.item;
        }

        return null;
    }

    getDropdownOption() {
        return [{
            id: this.item_id,
            text: this.item.description,
        }];
    }

    getTotal() {
        return this.total;
    }

    static getTotal(item) {
        return item.total;
    }

    getAdjustmentQty() {
        return this.item_adjustments.reduce((total, adjustment) => total + adjustment.qty, 0);
    }

    getAdjustmentPrice() {
        // eslint-disable-next-line radix
        return this.item_adjustments.reduce((total, adjustment) => total + parseInt(adjustment.price), 0);
    }

    getAdjustmentTotal() {
        return this.getTotal() - this.getAdjustmentPrice();
    }

    getLatestAdjustment() {
        return this.last_item_adjustment;
    }

    static getCreditInvoiceItemMap(i) {
        let children = [];
        if (i.children && i.children.length) {
            children = i.children.map((child) => {
                const latestAdjustment = child.getLatestAdjustment();
                const qty = latestAdjustment?.current_qty || latestAdjustment?.current_qty == 0 ? latestAdjustment?.current_qty : child.qty;
                const price = latestAdjustment?.current_price || latestAdjustment?.current_price == 0 ? latestAdjustment?.current_price : child.price;
                const discountPercentage = latestAdjustment?.current_discount_percentage || latestAdjustment?.current_discount_percentage == 0
                    ? latestAdjustment?.current_discount_percentage
                    : child.discount_percentage;
                const total = discountPercentage ? (qty * price) - ((qty * price) * discountPercentage) / 100 : qty * price;
                const taxAmount = latestAdjustment?.current_tax_amount || latestAdjustment?.current_tax_amount == 0
                    ? latestAdjustment?.current_tax_amount : child.tax_amount;

                return new InvoiceItem({
                    ...child,
                    old_qty: qty,
                    qty: qty,
                    old_price: formatToCurrency(price),
                    price: formatToCurrency(price),
                    old_total: formatToCurrency(total),
                    total: formatToCurrency(total),
                    cost: formatToCurrency(child.cost),
                    tax_amount: formatToCurrency(taxAmount),
                    previous_invoice_item_id: child.id,
                });
            });
        }

        const latestAdjustment = i.getLatestAdjustment();

        const qty = latestAdjustment?.current_qty || latestAdjustment?.current_qty == 0 ? latestAdjustment?.current_qty : i.qty;
        const price = latestAdjustment?.current_price || latestAdjustment?.current_price == 0 ? latestAdjustment?.current_price : i.price;
        const discountPercentage = latestAdjustment?.current_discount_percentage || latestAdjustment?.current_discount_percentage == 0
            ? latestAdjustment?.current_discount_percentage
            : i.discount_percentage;
        const total = discountPercentage ? (qty * price) - ((qty * price) * discountPercentage) / 100 : qty * price;
        const taxAmount = latestAdjustment?.current_tax_amount || latestAdjustment?.current_tax_amount == 0
            ? latestAdjustment?.current_tax_amount : i.tax_amount;

        return new InvoiceItem({
            ...i,
            old_qty: qty,
            qty: qty,
            previous_invoice_item_id: i.id,
            children,
            old_price: formatToCurrency(price),
            price: formatToCurrency(price),
            old_discount_percentage: discountPercentage,
            discount_percentage: discountPercentage,
            old_total: formatToCurrency(total),
            total: formatToCurrency(total),
            tax_amount: formatToCurrency(taxAmount),
            cost: formatToCurrency(i.cost),
        });
    }
}
